import React, { useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { motion } from 'framer-motion';
import { X, CreditCard, Smartphone, Check, Bitcoin, Loader2, AlertCircle } from 'lucide-react';
import { useToast } from '../../hooks/useToast';
import { formatCurrency } from '../../utils/formatters';
import { convertFCFAToEUR } from '../../utils/currencyConverter';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { checkOrderPaymentStatus } from '../../services/shop/orderPaymentService';
import { initiateOrderPayment } from '../../services/shop/orderPaymentService';
// import { usePaymentStatus } from '../../hooks/usePaymentStatus';
import { useOrderStatus } from '../../hooks/useOrderStatus';
import { supabase } from '../../lib/supabase';
import { sendOrderConfirmationEmail } from '../../services/shopEmailService';
import { useTenantContext } from '../../context/TenantContext';
import { useTenantPaymentProviders } from '../../hooks/useTenantPaymentProviders';

// Montant minimum pour les paiements internationaux
const MIN_INTERNATIONAL_PAYMENT_AMOUNT = 1500;
const MIN_LYGOS_PAYMENT_AMOUNT = 200; // Montant minimum en FCFA pour Lygos

function hexToRgba(hex: string, alpha = 1) {
  const cleaned = hex.replace('#', '');
  const bigint = parseInt(
    cleaned.length === 3
      ? cleaned
          .split('')
          .map((c) => c + c)
          .join('')
      : cleaned,
    16
  );
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}


interface PaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  order: {
    id: string;
    order_number: string;
    total_amount: number;
    customer_name: string;
    customer_email: string;
  };
  onPaymentSuccess: () => void;
}

export const PaymentModal: React.FC<PaymentModalProps> = ({
  isOpen,
  onClose,
  order,
  onPaymentSuccess
}) => {
  const [selectedPayment, setSelectedPayment] = useState<string | null>(null);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [processing, setProcessing] = useState(false);
  const [paymentReference, setPaymentReference] = useState<string>();
  const { toast } = useToast();
  const { tenant } = useTenantContext();
  const { data: paymentProviders } = useTenantPaymentProviders();
  
  // Suivre le statut de la commande et fermer le modal dès que le statut passe à "paid"
  useOrderStatus({
    orderId: order.id,
    onSuccess: () => {
      console.log('[PaymentModal] onSuccess déclenché, préparation de l\'envoi d\'email et de la redirection...');
      toast({ title: "Paiement réussi !", description: "Votre commande a été confirmée." });
      setProcessing(false); // Arrêter l'indicateur de traitement
      
      // Envoyer l'email de confirmation de commande AVANT la redirection
      console.log('[PaymentModal] Envoi de l\'email de confirmation pour la commande:', order.id);
      
      // Préparer la redirection immédiate sans attendre l'envoi d'email
      const orderId = order.id;
      const redirectUrl = `/boutique/confirmation/${orderId}`;
      console.log('[PaymentModal] Redirection immédiate vers:', redirectUrl);
      
      // Stocker l'ID de commande dans localStorage pour la page de confirmation
      localStorage.setItem('last_order_code', order.id.toString());
      
      // Redirection immédiate sans attendre l'envoi d'email
      toast({ title: "Paiement confirmé", description: "Redirection vers la page de confirmation..." });
      redirectToConfirmationPage();
      
      // Fonction de redirection vers la page de confirmation
      function redirectToConfirmationPage() {
        try {
          // Utiliser directement l'ID numérique pour la redirection
          const orderId = order.id;
          console.log('[PaymentModal] Détails de la commande pour redirection:', { 
            id: order.id, 
            order_number: order.order_number,
            status: 'paid'
          });
          
          // Forcer la redirection avec l'ID numérique uniquement
          const redirectUrl = `/boutique/confirmation/${orderId}`;
          console.log('[PaymentModal] Redirection vers:', redirectUrl);
          
          // Utiliser replace pour éviter les problèmes d'historique
          window.location.replace(redirectUrl);
        } catch (err) {
          console.error('[PaymentModal] Erreur lors de la redirection:', err);
          // Fallback en cas d'erreur - rediriger vers la boutique
          window.location.href = '/boutique';
        }
      }
    },

    onError: (error) => {
      console.error('[PaymentModal] Erreur de suivi de commande:', error);
      toast({ title: "Erreur de suivi", description: error.message, variant: "destructive" });
      setProcessing(false);
    },
  });

  const handlePhoneNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPhoneNumber(e.target.value);
    setPhoneError(null);
  };

  const handlePaymentMethodSelect = (method: string) => {
    setSelectedPayment(method);
    
    // Réinitialiser les erreurs de téléphone
    if (method !== 'mtn') {
      setPhoneError(null);
    }
  };

  const handlePaymentSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedPayment) {
      toast({ title: "Méthode de paiement requise", description: "Veuillez sélectionner une méthode de paiement", variant: "destructive" });
      return;
    }
    
    // Vérifier le numéro de téléphone pour le paiement mobile
    if (selectedPayment === 'mtn' && !phoneNumber) {
      setPhoneError('Numéro de téléphone requis');
      return;
    }
    
    // Vérifier le format du numéro pour MTN/Orange (format camerounais)
    if (selectedPayment === 'mtn' && !/^(237)?[6-9][0-9]{8}$/.test(phoneNumber)) {
      setPhoneError('Format invalide. Exemple: 237612345678');
      return;
    }
    
    setProcessing(true);
    
    try {
      // Utiliser le service de paiement spécifique à la boutique
      const response = await initiateOrderPayment(order, {
        paymentMethod: selectedPayment,
        phoneNumber: phoneNumber
      });
      
      // Stocker la référence de paiement pour le suivi
      setPaymentReference(response.payment_reference);
      
      // Si c'est un paiement qui nécessite une redirection (PayPal, Stripe, Lygos, Campay Card, etc.)
      if (response.payment_url && ['paypal', 'stripe', 'lygos', 'plisio', 'campay_card'].includes(selectedPayment)) {
        // Ouvrir dans le même onglet au lieu d'un nouvel onglet
        window.location.href = response.payment_url;
      }
      
      toast({ 
        title: "Paiement initié", 
        description: "Veuillez suivre les instructions pour finaliser votre paiement", 
        variant: "default" 
      });
      
    } catch (error) {
      console.error('Erreur lors de l\'initialisation du paiement:', error);
      toast({ 
        title: "Erreur", 
        description: error instanceof Error ? error.message : "Une erreur est survenue lors de l'initialisation du paiement", 
        variant: "destructive" 
      });
      setProcessing(false);
    }
  };

  return (
    <Dialog.Root open={isOpen} onOpenChange={onClose}>
      <Dialog.Portal>
        <Dialog.Overlay
          className="fixed inset-0 z-[100] animate-fade-in"
          style={{
            backgroundColor: hexToRgba('#000000', 0.6),
            backdropFilter: 'blur(16px)',
          }}
        />
        <Dialog.Content
          className="fixed top-[50%] left-[50%] translate-x-[-50%] translate-y-[-50%] w-[90vw] max-w-[450px] max-h-[85vh] rounded-xl backdrop-blur-md text-white focus:outline-none z-[101] overflow-y-auto p-6 Dialog__Content"
          style={{
            background: `linear-gradient(to bottom, ${hexToRgba('#ffffff', 0.1)}, ${hexToRgba('#000000', 0.3)})`,
            boxShadow: '0 0 50px rgba(255,255,255,0.1)',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            WebkitOverflowScrolling: 'touch'
          }}
        >
          <style>{`
            .Dialog__Content::-webkit-scrollbar {
              display: none;
            }
          `}</style>
          <div className="flex items-center justify-between mb-4">
            <Dialog.Title className="text-xl font-bold text-white">
              Paiement de votre commande
            </Dialog.Title>
            <Dialog.Close asChild>
              <button className="rounded-full p-1 hover:bg-white/10 transition-colors">
                <X className="h-5 w-5 text-white" />
              </button>
            </Dialog.Close>
          </div>

          <div className="mb-6">
            <div
              className="p-4 rounded-xl mb-4 border backdrop-blur-sm"
              style={{
                background: `linear-gradient(135deg, ${hexToRgba(
                  tenant?.config?.color1 || '#f97316',
                  0.15
                )}, ${hexToRgba(tenant?.config?.color2 || '#ef4444', 0.15)})`,
                borderColor: hexToRgba('#ffffff', 0.2),
              }}
            >
              <p className="text-sm text-white/70">Montant à payer</p>
              <p className="text-2xl font-bold text-white">
                {formatCurrency(order.total_amount)}
              </p>
              <p className="text-xs text-white/60">Commande #{order.order_number}</p>
            </div>
          </div>

          <form onSubmit={handlePaymentSubmit}>
            <p className="text-lg font-medium mb-4 text-white">
              Choisissez votre méthode de paiement
            </p>
            <div className="space-y-3 mb-6">
              {(paymentProviders || []).map((method) => (
                <div key={method.id}>
                  <button
                    type="button"
                    onClick={() => handlePaymentMethodSelect(method.id)}
                    disabled={method.min_amount && order.total_amount < method.min_amount}
                    className="relative w-full flex items-center p-3 rounded-xl border-2 transition-all overflow-hidden disabled:opacity-50 disabled:cursor-not-allowed backdrop-blur-sm"
                    style={{
                      borderColor:
                        selectedPayment === method.id
                          ? hexToRgba('#ffffff', 0.5)
                          : hexToRgba('#ffffff', 0.2),
                      backgroundColor:
                        selectedPayment === method.id
                          ? hexToRgba('#ffffff', 0.15)
                          : hexToRgba('#ffffff', 0.05),
                    }}
                  >
                    <div className="relative flex items-center w-full">
                      <div
                        className="w-12 h-12 mr-3 rounded-md flex items-center justify-center backdrop-blur-sm"
                        style={{ backgroundColor: hexToRgba('#ffffff', 0.1) }}
                      >
                        <img
                          src={method.logo_url}
                          alt={method.name}
                          className="h-8 object-contain"
                        />
                      </div>
                      <div className="flex-1 text-left">
                        <span className="font-semibold block text-white">{method.name}</span>
                        <p className="text-xs text-white/60">{method.description}</p>
                        {method.min_amount && order.total_amount < method.min_amount && (
                          <p className="text-xs text-red-400 mt-1">
                            ⚠️ Montant minimum : {formatCurrency(method.min_amount)}
                          </p>
                        )}
                      </div>
                      {selectedPayment === method.id && (
                        <Check className="w-5 h-5 ml-auto text-white" />
                      )}
                    </div>
                  </button>

                  {/* Message explicatif pour Lygos */}
                  {selectedPayment === method.id && method.id === 'lygos' && method.countries && method.countries.length > 0 && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      className="mt-3 mb-3 p-4 rounded-b-xl border-t backdrop-blur-sm"
                      style={{
                        backgroundColor: hexToRgba('#3b82f6', 0.1),
                        borderColor: hexToRgba('#3b82f6', 0.3),
                      }}
                    >
                      <p className="text-sm text-white/90 mb-2">
                        <span className="font-medium">💡 Lygos Pay</span> - Payez avec votre Mobile Money en Afrique
                      </p>
                      <div className="grid grid-cols-2 gap-2 text-xs text-white/70">
                        {method.countries.map((country) => (
                          <div key={country.country_code}>
                            {country.country_flag} {country.country_name} ({country.dial_code})
                          </div>
                        ))}
                      </div>
                      <p className="text-xs text-white/60 mt-3 italic">
                        ✓ Vous serez redirigé vers une page sécurisée pour choisir votre pays et finaliser le paiement.
                      </p>
                    </motion.div>
                  )}

                  {selectedPayment === method.id && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      className="mt-3 mb-3 space-y-4"
                    >
                      {method.id === 'mtn' && (
                        <div className="p-4 rounded-b-xl border-t backdrop-blur-sm"
                          style={{
                            backgroundColor: hexToRgba('#ffffff', 0.08),
                            borderColor: hexToRgba('#ffffff', 0.15),
                          }}
                        >
                          <label className="block text-sm font-medium mb-2 text-white">
                            Numéro de téléphone
                          </label>
                          <Input
                            type="tel"
                            value={phoneNumber}
                            onChange={handlePhoneNumberChange}
                            placeholder="237xxxxxxxxx"
                            className="w-full bg-white/10 border-white/20 text-white placeholder:text-white/40"
                            style={{
                              borderColor: phoneError
                                ? '#ef4444'
                                : hexToRgba('#ffffff', 0.3),
                            }}
                          />
                          {phoneError && (
                            <div className="flex items-center gap-2 mt-2 text-red-400 text-sm">
                              <AlertCircle className="w-4 h-4" />
                              <span>{phoneError}</span>
                            </div>
                          )}
                          <p className="text-xs text-white/60 mt-2">
                            Vous recevrez une notification pour confirmer le paiement
                          </p>
                        </div>
                      )}

                      <Button
                        type="submit"
                        disabled={!selectedPayment || processing || (method.id === 'mtn' && !phoneNumber)}
                        className="w-full py-3 rounded-xl font-semibold text-white transition-all backdrop-blur-sm border border-white/20"
                        style={{
                          backgroundColor: hexToRgba(tenant?.config?.color1 || '#f97316', 0.8),
                          opacity: !selectedPayment || processing || (method.id === 'mtn' && !phoneNumber) ? 0.5 : 1,
                        }}
                      >
                        {processing ? (
                          <>
                            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                            Traitement...
                          </>
                        ) : (
                          `Payer ${['paypal', 'stripe', 'plisio'].includes(method.id)
                            ? `€${convertFCFAToEUR(order.total_amount).toFixed(2)}`
                            : `${formatCurrency(order.total_amount)}`}`
                        )}
                      </Button>
                    </motion.div>
                  )}
                </div>
              ))}
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
